"use client"

import { useRef, useEffect, useState } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

export default function HorizontalScrollMenu({ menuItems = [], onItemClick, selectedItem }) {
  const scrollRef = useRef(null)
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollStart = useRef(0)
  const [showSwipeIndicator, setShowSwipeIndicator] = useState(true)
  const [showLeftFade, setShowLeftFade] = useState(false)
  const [showRightFade, setShowRightFade] = useState(true)
  const [isArrowScrolling, setIsArrowScrolling] = useState(false)

  useEffect(() => {
    const el = scrollRef.current

    const updateFades = () => {
      if (!el) return
      setShowLeftFade(el.scrollLeft > 10)
      setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 10)
    }

    const onMouseDown = (e) => {
      isDragging.current = true
      el.classList.add("cursor-grabbing", "select-none")
      startX.current = e.pageX
      scrollStart.current = el.scrollLeft
      setShowSwipeIndicator(false)
    }

    const onMouseMove = (e) => {
      if (!isDragging.current) return
      e.preventDefault()
      const x = e.pageX
      const walk = (x - startX.current) * 2 // Adjust scroll speed
      el.scrollLeft = scrollStart.current - walk
    }

    const stopDrag = () => {
      isDragging.current = false
      el.classList.remove("cursor-grabbing", "select-none")
    }

    const onTouchStart = () => setShowSwipeIndicator(false)
    const onScroll = () => {
      setShowSwipeIndicator(false)
      updateFades()
    }

    el.addEventListener("mousedown", onMouseDown)
    el.addEventListener("mousemove", onMouseMove)
    el.addEventListener("mouseup", stopDrag)
    el.addEventListener("mouseleave", stopDrag)
    el.addEventListener("scroll", onScroll)
    el.addEventListener("touchstart", onTouchStart)

    updateFades()
    const timer = setTimeout(() => setShowSwipeIndicator(false), 3000)

    return () => {
      el.removeEventListener("mousedown", onMouseDown)
      el.removeEventListener("mousemove", onMouseMove)
      el.removeEventListener("mouseup", stopDrag)
      el.removeEventListener("mouseleave", stopDrag)
      el.removeEventListener("scroll", onScroll)
      el.removeEventListener("touchstart", onTouchStart)
      clearTimeout(timer)
    }
  }, [])

  const scrollByAmount = (amount) => {
    if (isArrowScrolling) return
    setIsArrowScrolling(true)
    scrollRef.current?.scrollBy({ left: amount, behavior: "smooth" })
    setTimeout(() => setIsArrowScrolling(false), 500)
  }

  return (
    <div className="relative">
      {/* Swipe Indicator */}
      {showSwipeIndicator && (
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-8 z-20">
          <div className="flex items-center space-x-2 bg-black bg-opacity-75 text-white px-3 py-1 rounded-full text-sm animate-bounce">
            <ChevronLeft className="w-4 h-4 animate-pulse" />
            <span>Swipe to browse</span>
            <ChevronRight className="w-4 h-4 animate-pulse" />
          </div>
        </div>
      )}

      {/* Arrows */}
      {showLeftFade && (
        <button
          onClick={() => scrollByAmount(-500)}
          className="absolute md:block hidden left-0 top-1/2 -translate-y-1/2 z-30 bg-[#FB923C] text-white p-1 rounded-full shadow-md hover:scale-110 transition-transform"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}
      {showRightFade && (
        <button
          onClick={() => scrollByAmount(500)}
          className="absolute md:block hidden right-0 top-1/2 -translate-y-1/2 z-30 bg-[#FB923C] text-white p-1 rounded-full shadow-md hover:scale-110 transition-transform"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Scroll Menu */}
      <div
        ref={scrollRef}
        className="flex md:gap-12 gap-4 overflow-x-auto whitespace-nowrap cursor-grab scrollbar-hide scroll-smooth"
      >
        {menuItems.map((item, idx) => (
          <div
            key={idx}
            onClick={() => {
              onItemClick(item)
              setShowSwipeIndicator(false)
            }}
            className={`text-black md:text-3xl text-base font-bold font-['Roboto'] leading-0 min-w-max cursor-pointer transition-all duration-200 ${selectedItem === item ? "underline text-black" : "hover:text-gray-600"
              }`}
          >
            {item}
          </div>
        ))}
      </div>

      {/* Gradient fades */}
      {showLeftFade && (
        <div className="absolute top-0 left-0 w-12 h-full bg-gradient-to-r from-[#FB923C] via-[#FB923Ccc] to-transparent pointer-events-none z-10" />
      )}
      {showRightFade && (
        <div className="absolute top-0 right-0 w-12 h-full bg-gradient-to-l from-[#FB923C] via-[#FB923Ccc] to-transparent pointer-events-none z-10" />
      )}
    </div>
  )
}
